/**
 * Network Speed Detection Utility
 * Provides various methods to estimate internet connection speed
 */

/**
 * Get connection info from Navigator API (if available)
 */
export const getConnectionInfo = () => {
  try {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection) {
      console.log('📡 Raw connection object:', connection);
      
      const info = {
        effectiveType: connection.effectiveType, // '4g', '3g', '2g', 'slow-2g'
        downlink: connection.downlink, // Mbps
        rtt: connection.rtt, // Round trip time in ms
        saveData: connection.saveData, // Data saver mode
        type: connection.type // 'wifi', 'cellular', etc.
      };
      
      console.log('📡 Parsed connection info:', info);
      return info;
    }
    
    console.log('📡 No connection API available');
    return null;
  } catch (error) {
    console.error('❌ Error getting connection info:', error);
    return null;
  }
};

/**
 * Estimate network speed by downloading a small image
 * Returns speed in Mbps
 */
export const measureDownloadSpeed = async (testImageUrl = null, timeoutMs = 3000) => {
  console.log('🚀 Starting download speed test...');
  
  return new Promise((resolve) => {
    const startTime = performance.now();
    const img = new Image();
    let resolved = false;
    
    const resolveOnce = (result) => {
      if (!resolved) {
        resolved = true;
        resolve(result);
      }
    };
    
    // Use multiple fallback URLs for better reliability
    const testUrls = [
      testImageUrl,
      // Small image from a reliable CDN
      'https://via.placeholder.com/1x1.png',
      // Fallback to data URL
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
    ].filter(Boolean);
    
    let currentUrlIndex = 0;
    
    // Timeout fallback
    const timeout = setTimeout(() => {
      console.warn('⏰ Speed test timeout');
      resolveOnce({
        speed_mbps: null,
        error: 'timeout',
        method: 'download_test',
        timeout_ms: timeoutMs
      });
    }, timeoutMs);
    
    const tryNextUrl = () => {
      if (currentUrlIndex >= testUrls.length) {
        clearTimeout(timeout);
        console.warn('❌ All test URLs failed');
        resolveOnce({
          speed_mbps: null,
          error: 'all_urls_failed',
          method: 'download_test',
          attempted_urls: testUrls.length
        });
        return;
      }
      
      const currentUrl = testUrls[currentUrlIndex];
      console.log(`🔄 Trying URL ${currentUrlIndex + 1}/${testUrls.length}:`, currentUrl);
      
      // Add cache busting
      const cacheBuster = Date.now() + Math.random();
      const urlWithCacheBuster = currentUrl.includes('data:') 
        ? currentUrl 
        : `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}t=${cacheBuster}`;
      
      img.src = urlWithCacheBuster;
      currentUrlIndex++;
    };
    
    img.onload = () => {
      clearTimeout(timeout);
      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000; // Convert to seconds
      
      console.log(`✅ Image loaded in ${duration.toFixed(3)}s`);
      
      // Estimate based on a small payload (very rough estimate)
      const estimatedBytes = 1024; // Assume 1KB for basic test
      const speedBps = estimatedBytes / duration;
      const speedMbps = (speedBps * 8) / (1024 * 1024); // Convert to Mbps
      
      resolveOnce({
        speed_mbps: parseFloat(speedMbps.toFixed(2)),
        duration_ms: parseFloat((endTime - startTime).toFixed(2)),
        method: 'download_test',
        url_used: img.src,
        estimated_bytes: estimatedBytes
      });
    };
    
    img.onerror = (error) => {
      console.warn(`❌ Image load failed for URL ${currentUrlIndex}:`, error);
      tryNextUrl(); // Try next URL
    };
    
    // Start with first URL
    tryNextUrl();
  });
};

/**
 * Simple, reliable network detection that always works
 */
export const getBasicNetworkInfo = () => {
  console.log('🔧 Using basic network detection...');
  
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  
  return {
    connection_type: connection?.effectiveType || 'unknown',
    downlink_mbps: connection?.downlink || null,
    rtt_ms: connection?.rtt || null,
    save_data_mode: connection?.saveData || false,
    network_type: connection?.type || 'unknown',
    online_status: navigator.onLine,
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    has_connection_api: !!connection,
    supports_performance_api: !!window.performance,
    method: 'basic_detection'
  };
};

/**
 * Simplified, reliable network information getter
 * This version prioritizes reliability over comprehensive data
 */
export const getNetworkInfo = async () => {
  console.log('🌐 Starting simplified network detection...');
  
  try {
    // Start with the most basic info that should always work
    const result = {
      // Basic browser info (always available)
      online_status: navigator.onLine,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      
      // Initialize with defaults
      connection_type: 'unknown',
      downlink_mbps: null,
      rtt_ms: null,
      save_data_mode: false,
      network_type: 'unknown',
      has_connection_api: false,
      supports_performance_api: !!window.performance,
      detection_method: 'basic_only'
    };
    
    console.log('📱 Basic result initialized:', result);
    
    // Try to enhance with Connection API if available
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      console.log('📡 Connection API available, enhancing data...');
      result.has_connection_api = true;
      result.connection_type = connection.effectiveType || 'unknown';
      result.downlink_mbps = connection.downlink || null;
      result.rtt_ms = connection.rtt || null;
      result.save_data_mode = connection.saveData || false;
      result.network_type = connection.type || 'unknown';
      result.detection_method = 'enhanced';
      console.log('📡 Enhanced result:', result);
    } else {
      console.log('📡 No Connection API available, using basic info only');
    }
    
    // Skip speed test for now to avoid any async issues
    result.measured_speed_mbps = null;
    result.speed_test_error = 'skipped_for_reliability';
    
    console.log('✅ Final simplified network info:', result);
    return result;
    
  } catch (error) {
    console.error('❌ Even simplified network detection failed:', error);
    
    // Return absolute minimum that should always work
    return {
      online_status: true, // Assume online if we got this far
      timestamp: new Date().toISOString(),
      connection_type: 'unknown',
      detection_method: 'emergency_fallback',
      error: error.message,
      error_stack: error.stack
    };
  }
};

/**
 * Get a simplified network speed category
 */
export const getNetworkSpeedCategory = (networkInfo) => {
  // Priority: Use Navigator API effective type if available
  if (networkInfo.connection_type && networkInfo.connection_type !== 'unknown') {
    return networkInfo.connection_type; // '4g', '3g', '2g', 'slow-2g'
  }
  
  // Fallback: Use downlink speed if available
  if (networkInfo.downlink_mbps) {
    const speed = parseFloat(networkInfo.downlink_mbps);
    if (speed >= 10) return 'fast';
    if (speed >= 1.5) return 'medium';
    if (speed >= 0.6) return 'slow';
    return 'very-slow';
  }
  
  // Last resort: Use measured speed
  if (networkInfo.measured_speed_mbps) {
    const speed = parseFloat(networkInfo.measured_speed_mbps);
    if (speed >= 10) return 'fast';
    if (speed >= 1.5) return 'medium';
    if (speed >= 0.6) return 'slow';
    return 'very-slow';
  }
  
  return 'unknown';
};

/**
 * Monitor network changes and return current status
 */
export const createNetworkMonitor = (callback) => {
  const handleOnline = () => callback({ online: true, timestamp: Date.now() });
  const handleOffline = () => callback({ online: false, timestamp: Date.now() });
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Also monitor connection changes if supported
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection) {
    const handleConnectionChange = () => {
      callback({
        online: navigator.onLine,
        connectionType: connection.effectiveType,
        downlink: connection.downlink,
        timestamp: Date.now()
      });
    };
    
    connection.addEventListener('change', handleConnectionChange);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      connection.removeEventListener('change', handleConnectionChange);
    };
  }
  
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};
